import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import { useMsal } from "@azure/msal-react";

import { FileText, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";

import { callApi } from "../api/api";

import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";

const POLICY_SUGGEST_MIN_LENGTH = 2;
const POLICY_SUGGEST_MAX = 5;
const POLICY_SUGGEST_DEBOUNCE_MS = 300;

function normalizePolicyQueryParam(value) {
  const trimmed = String(value || "").trim();

  if (!trimmed) {
    return "";
  }

  return trimmed.replace(/\s+/g, "").toUpperCase();
}

function transformSearchResults(apiResults) {
  const grouped = {};

  apiResults.forEach((r) => {
    if (!grouped[r.msg_id]) {
      grouped[r.msg_id] = [];
    }

    grouped[r.msg_id].push(r);
  });

  const transformed = [];

  Object.values(grouped).forEach((items) => {
    const first = items[0];

    if (first.email_storage_path) {
      transformed.push({
        ...first,
        row_type: "EMAIL",
        file_path: first.email_storage_path,
        file_name: first.email_storage_path.split("/").pop(),
      });
    }

    items.forEach((att) => {
      if (att.attachment_storage_path) {
        transformed.push({
          ...att,
          row_type: "ATTACHMENT",
          file_path: att.attachment_storage_path,
          file_name: att.attachment_storage_path.split("/").pop(),
        });
      }
    });
  });

  return transformed;
}

function getPolicyKey(policyno) {
  return policyno?.trim() || "";
}

function buildPolicySummaryRows(documents) {
  const byPolicy = new Map();

  for (const doc of documents) {
    const policyKey = getPolicyKey(doc.policyno);

    if (!byPolicy.has(policyKey)) {
      byPolicy.set(policyKey, {
        policyKey,
        policyno: doc.policyno || "",
        mail_from_address: doc.mail_from_address,
        document_type: doc.document_type,
        documentTypes: new Set(),
        verified_at: doc.verified_at,
        documentCount: 0,
      });
    }

    const group = byPolicy.get(policyKey);

    group.documentCount += 1;

    if (doc.document_type) {
      group.documentTypes.add(doc.document_type);
    }

    const docTime = doc.verified_at ? new Date(doc.verified_at).getTime() : 0;
    const groupTime = group.verified_at
      ? new Date(group.verified_at).getTime()
      : 0;

    if (docTime >= groupTime) {
      group.verified_at = doc.verified_at;
      group.mail_from_address = doc.mail_from_address;
      group.document_type = doc.document_type;
    }
  }

  return Array.from(byPolicy.values())
    .map((group) => ({
      policyKey: group.policyKey,
      policyno: group.policyno,
      mail_from_address: group.mail_from_address,
      document_type:
        group.documentTypes.size > 1 ? "Multiple types" : group.document_type,
      verified_at: group.verified_at,
      documentCount: group.documentCount,
    }))
    .sort((a, b) => {
      const timeA = a.verified_at ? new Date(a.verified_at).getTime() : 0;
      const timeB = b.verified_at ? new Date(b.verified_at).getTime() : 0;

      return timeB - timeA;
    });
}

function getMessageGroupKey(doc) {
  const msgId = doc.msg_id?.trim();

  if (msgId) {
    return msgId;
  }

  return `manual-${doc.file_path || doc.file_name || "unknown"}`;
}

function buildMessageGroups(documents) {
  const map = new Map();

  for (const doc of documents) {
    const groupKey = getMessageGroupKey(doc);

    if (!map.has(groupKey)) {
      map.set(groupKey, {
        groupKey,
        msg_id: doc.msg_id,
        policyno: doc.policyno,
        document_type: doc.document_type,
        verified_at: doc.verified_at,
        email_subject: doc.email_subject,
        mail_from_address: doc.mail_from_address,
        files: [],
      });
    }

    map.get(groupKey).files.push({
      file_name: doc.file_name,
      file_path: doc.file_path,
      row_type: doc.row_type,
      effective_access: doc.effective_access,
    });
  }

  return Array.from(map.values()).sort((a, b) => {
    const timeA = a.verified_at ? new Date(a.verified_at).getTime() : 0;
    const timeB = b.verified_at ? new Date(b.verified_at).getTime() : 0;

    return timeB - timeA;
  });
}

function extractPolicySuggestions(apiResults) {
  const seen = new Set();
  const suggestions = [];

  for (const row of apiResults) {
    const policy = row.policyno?.trim();

    if (!policy || seen.has(policy)) {
      continue;
    }

    seen.add(policy);
    suggestions.push(policy);

    if (suggestions.length >= POLICY_SUGGEST_MAX) {
      break;
    }
  }

  return suggestions;
}

export default function SearchDocuments() {
  const { instance, accounts } = useMsal();

  /* =========================
     SEARCH FIELDS
  ========================= */

  const [policyNo, setPolicyNo] = useState("");

  const [email, setEmail] = useState("");

  const [insured, setInsured] = useState("");

  const [agencyNo, setAgencyNo] = useState("");

  const FILESYSTEM = process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";
  /* =========================
     DATA
  ========================= */

  const [results, setResults] = useState([]);

  const [resultCount, setResultCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [hasSearched, setHasSearched] = useState(false);

  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const [expandedMessages, setExpandedMessages] = useState({});

  const [policySuggestions, setPolicySuggestions] = useState([]);

  const [showPolicyDropdown, setShowPolicyDropdown] = useState(false);

  const [suggestLoading, setSuggestLoading] = useState(false);

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const policyDropdownRef = useRef(null);

  const suggestControllerRef = useRef(null);

  const searchControllerRef = useRef(null);

  const skipSuggestFetchRef = useRef(false);

  const suggestRequestIdRef = useRef(0);

  /* =========================
     PREVIEW
  ========================= */

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [previewUrl, setPreviewUrl] = useState(null);

  const [previewError, setPreviewError] = useState(null);

  const [previewLoading, setPreviewLoading] = useState(false);

  const [downloadLoading, setDownloadLoading] = useState(false);

  const [fileTooltip, setFileTooltip] = useState(null);

  const showFileTooltip = useCallback((event, fileName) => {
    if (!fileName) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    setFileTooltip({
      text: fileName,
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const hideFileTooltip = useCallback(() => {
    setFileTooltip(null);
  }, []);

  useEffect(() => {
    if (!fileTooltip) {
      return undefined;
    }

    const hide = () => setFileTooltip(null);

    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [fileTooltip]);

  useEffect(() => {
    if (!showPolicyDropdown) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        policyDropdownRef.current &&
        !policyDropdownRef.current.contains(event.target)
      ) {
        setShowPolicyDropdown(false);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPolicyDropdown]);

  useEffect(() => {
    return () => {
      if (suggestControllerRef.current) {
        suggestControllerRef.current.abort();
      }

      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }
    };
  }, []);

  /* =========================
     SEARCH
  ========================= */

  const executeSearch = useCallback(
    async (options = {}) => {
      const activePolicyNo =
        options.policyno !== undefined ? options.policyno : policyNo;
      const activeEmail =
        options.mail_from_address !== undefined
          ? options.mail_from_address
          : email;
      const activeInsured =
        options.primary_insured !== undefined
          ? options.primary_insured
          : insured;
      const activeAgencyNo =
        options.agency_no !== undefined ? options.agency_no : agencyNo;

      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }

      const controller = new AbortController();

      searchControllerRef.current = controller;

      try {
        setError("");
        setShowPolicyDropdown(false);
        setActiveSuggestionIndex(-1);

        if (!activePolicyNo && !activeEmail && !activeInsured && !activeAgencyNo) {
          setError("Enter at least one search field");
          setResults([]);
          setResultCount(0);
          setSelectedPolicy(null);
          setHasSearched(false);

          return;
        }

        setHasSearched(true);
        setSelectedPolicy(null);
        setLoading(true);
        setResults([]);
        setResultCount(0);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/admin/search",
          null,
          {
            signal: controller.signal,
            params: {
              policyno: activePolicyNo
                ? normalizePolicyQueryParam(activePolicyNo)
                : activePolicyNo,
              mail_from_address: activeEmail,
              primary_insured: activeInsured,
              agency_no: activeAgencyNo,
            },
          },
        );

        if (controller.signal.aborted) {
          return;
        }

        const apiResults = res?.data?.data || [];
        const transformed = transformSearchResults(apiResults);

        setResults(transformed);
        setResultCount(
          typeof res?.data?.count === "number"
            ? res.data.count
            : transformed.length,
        );
      } catch (err) {
        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error(err);

        setError(err.message || "Search failed");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [instance, accounts, policyNo, email, insured, agencyNo],
  );

  const runSearch = () => {
    executeSearch();
  };

  const handlePolicySuggestionSelect = useCallback(
    (selectedPolicy) => {
      skipSuggestFetchRef.current = true;
      setPolicyNo(selectedPolicy);
      setPolicySuggestions([]);
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      executeSearch({ policyno: selectedPolicy });
    },
    [executeSearch],
  );

  const fetchPolicySuggestions = useCallback(
    async (query) => {
      const trimmed = query.trim();

      if (trimmed.length < POLICY_SUGGEST_MIN_LENGTH) {
        setPolicySuggestions([]);
        setShowPolicyDropdown(false);
        setActiveSuggestionIndex(-1);
        return;
      }

      if (suggestControllerRef.current) {
        suggestControllerRef.current.abort();
      }

      const controller = new AbortController();

      suggestControllerRef.current = controller;
      const requestId = ++suggestRequestIdRef.current;

      try {
        setSuggestLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/admin/search",
          null,
          {
            signal: controller.signal,
            params: { policyno: normalizePolicyQueryParam(trimmed) },
          },
        );

        if (
          controller.signal.aborted ||
          requestId !== suggestRequestIdRef.current
        ) {
          return;
        }

        const suggestions = extractPolicySuggestions(res?.data?.data || []);

        setPolicySuggestions(suggestions);
        setShowPolicyDropdown(true);
        setActiveSuggestionIndex(-1);
      } catch (err) {
        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error("Policy suggestion lookup failed", err);
        setPolicySuggestions([]);
      } finally {
        if (
          !controller.signal.aborted &&
          requestId === suggestRequestIdRef.current
        ) {
          setSuggestLoading(false);
        }
      }
    },
    [instance, accounts],
  );

  useEffect(() => {
    if (skipSuggestFetchRef.current) {
      skipSuggestFetchRef.current = false;
      return undefined;
    }

    const trimmed = policyNo.trim();

    if (!trimmed) {
      setPolicySuggestions([]);
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      return undefined;
    }

    if (trimmed.length < POLICY_SUGGEST_MIN_LENGTH) {
      setPolicySuggestions([]);
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      return undefined;
    }

    const timer = setTimeout(() => {
      fetchPolicySuggestions(trimmed);
    }, POLICY_SUGGEST_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [policyNo, fetchPolicySuggestions]);

  const handlePolicyInputChange = (e) => {
    setPolicyNo(e.target.value);
    setShowPolicyDropdown(true);
  };

  const handlePolicyInputKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    if (e.key === "ArrowDown") {
      if (!showPolicyDropdown || policySuggestions.length === 0) {
        return;
      }

      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev < policySuggestions.length - 1 ? prev + 1 : 0,
      );
      return;
    }

    if (e.key === "ArrowUp") {
      if (!showPolicyDropdown || policySuggestions.length === 0) {
        return;
      }

      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev > 0 ? prev - 1 : policySuggestions.length - 1,
      );
      return;
    }

    if (e.key === "Enter") {
      if (showPolicyDropdown && policySuggestions.length > 0) {
        e.preventDefault();
        const index =
          activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0;
        handlePolicySuggestionSelect(policySuggestions[index]);
        return;
      }

      e.preventDefault();
      runSearch();
    }
  };

  /* =========================
     DOWNLOAD
  ========================= */

  const handleDownload = useCallback(async (path) => {
    /*
    Prevent rapid spam downloads
  */

    if (downloadLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setDownloadLoading(true);

      const cleaned = path?.trim();

      if (!cleaned) {
        return;
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      /*
      TOKEN
    */

      const tokenResponse = await instance.acquireTokenSilent({
        account: instance.getActiveAccount(),
      });

      /*
      DOWNLOAD
    */

      const response = await fetch(
        `${baseUrl}/api/adls/download` +
          `?filesystem=${encodeURIComponent(filesystem)}` +
          `&path=${encodeURIComponent(filePath)}`,
        {
          method: "GET",

          signal: controller.signal,

          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      /*
      Empty response protection
    */

      if (!blob || blob.size === 0) {
        throw new Error("Empty download response");
      }

      /*
      FILENAME
    */

      const fileName = filePath.split("/").pop() || "download";

      /*
      DOWNLOAD
    */

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = blobUrl;

      link.download = fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      /*
      Cleanup
    */

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (err) {
      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error("Download failed", err);

      setError(err.message || "Download failed");
    } finally {
      controller.abort();

      setDownloadLoading(false);
    }
  }, [instance, FILESYSTEM, downloadLoading]);

  /* =========================
     PREVIEW
  ========================= */

  const handlePreview = useCallback(async (path) => {
    /*
    Prevent rapid preview spam
  */

    if (previewLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setPreviewLoading(true);

      setIsPreviewOpen(true);

      setPreviewError(null);

      /*
      Cleanup previous blob URL
    */

      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(null);

      const cleaned = path?.trim();

      if (!cleaned) {
        throw new Error("Invalid path");
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const lower = filePath.toLowerCase();

      const isTiff = lower.endsWith(".tif") || lower.endsWith(".tiff");

      const isEml = lower.endsWith(".eml");

      const isHeic = lower.endsWith(".heic") || lower.endsWith(".heif");

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      const BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_CLIENT_ID;

      /* =========================
       TIFF
    ========================= */

      if (isTiff || isEml || isHeic) {
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${BACKEND_CLIENT_ID}/access_as_user`],
        });

        const response = await fetch(
          `${baseUrl}/api/adls/preview-blob` +
            `?filesystem=${encodeURIComponent(filesystem)}` +
            `&path=${encodeURIComponent(filePath)}`,
          {
            method: "GET",
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to preview file (${response.status})`);
        }

        const blob = await response.blob();

        const blobUrl = URL.createObjectURL(blob);

        setPreviewUrl(blobUrl);

        return;
      }

      /* =========================
       NORMAL FILES
    ========================= */

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/adls/preview-url",
        null,
        {
          signal: controller.signal,

          params: {
            filesystem,
            path: filePath,
          },
        },
      );

      if (!res?.data?.url) {
        throw new Error("Preview URL missing");
      }

      setPreviewUrl(res.data.url);
    } catch (err) {
      /*
      Ignore cancellation
    */

      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error("Preview failed", err);

      setPreviewError(err.message || "Preview failed");
    } finally {
      controller.abort();

      setPreviewLoading(false);
    }
  }, [instance, accounts, FILESYSTEM, previewLoading, previewUrl]);

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setIsPreviewOpen(false);

    setPreviewUrl(null);

    setPreviewError(null);
  };

  /* =========================
     DRILL-DOWN
  ========================= */

  const policyDocuments = useMemo(() => {
    if (selectedPolicy === null) {
      return [];
    }

    return results.filter((doc) => getPolicyKey(doc.policyno) === selectedPolicy);
  }, [results, selectedPolicy]);

  const policySummaryRows = useMemo(
    () => buildPolicySummaryRows(results),
    [results],
  );

  const messageGroups = useMemo(
    () => buildMessageGroups(policyDocuments),
    [policyDocuments],
  );

  useEffect(() => {
    if (selectedPolicy === null) {
      setExpandedMessages({});
      return;
    }

    const nextExpanded = {};

    messageGroups.forEach((group) => {
      nextExpanded[group.groupKey] = true;
    });

    setExpandedMessages(nextExpanded);
  }, [selectedPolicy, messageGroups]);

  const toggleMessageExpand = useCallback((groupKey) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  }, []);

  const handlePolicyRowSelect = useCallback((row) => {
    setSelectedPolicy(row.policyKey ?? getPolicyKey(row.policyno));
  }, []);

  const handleBackToResults = useCallback(() => {
    setSelectedPolicy(null);
  }, []);

  const handlePolicyRowKeyDown = useCallback(
    (event, row) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handlePolicyRowSelect(row);
      }
    },
    [handlePolicyRowSelect],
  );

  /* =========================
     RESPONSIVE CARD MAPPERS
  ========================= */

  const mapPolicyCard = useCallback((row) => {
    return {
      id: `policy-${row.policyKey}`,
      icon: <FileText size={16} />,
      tone: "info",
      title: row.policyno || "—",
      subtitle: row.mail_from_address || "—",
      meta: [
        {
          label: "Documents",
          value: String(row.documentCount),
        },
        {
          label: "Type",
          value: row.document_type || "—",
        },
        {
          label: "Date",
          value: row.verified_at
            ? new Date(row.verified_at).toLocaleDateString()
            : "—",
        },
      ],
    };
  }, []);

  const renderResultsTable = useCallback(
    () => {
      if (loading) return <div style={styles.empty}>Searching...</div>;
      if (policySummaryRows.length === 0)
        return <div style={styles.empty}>No results found</div>;
      return (
        <ScrollableTable style={styles.tableWrap}>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: "18%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={styles.th}>POLICY NUMBER</th>

              <th style={styles.th}>MAIL FROM</th>

              <th style={styles.th}>DOCUMENT TYPE</th>

              <th style={styles.th}>DATE</th>

              <th style={styles.th}>DOCUMENTS</th>

              <th style={styles.thAction} aria-label="Actions" />
            </tr>
          </thead>

          <tbody>
            {policySummaryRows.map((row) => (
              <tr
                key={row.policyKey || "unknown-policy"}
                style={styles.clickableRow}
                role="button"
                tabIndex={0}
                aria-label={`View ${row.documentCount} document${
                  row.documentCount === 1 ? "" : "s"
                } for policy ${row.policyno || "unknown"}`}
                onClick={() => handlePolicyRowSelect(row)}
                onKeyDown={(event) => handlePolicyRowKeyDown(event, row)}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = "#eff6ff";
                  event.currentTarget.style.boxShadow = "inset 3px 0 0 #2563eb";

                  const policyLabel =
                    event.currentTarget.querySelector("[data-policy-label]");

                  if (policyLabel) {
                    policyLabel.style.color = "#1d4ed8";
                  }

                  const viewAction =
                    event.currentTarget.querySelector("[data-view-action]");

                  if (viewAction) {
                    viewAction.style.color = "#1d4ed8";
                  }
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "";
                  event.currentTarget.style.boxShadow = "";

                  const policyLabel =
                    event.currentTarget.querySelector("[data-policy-label]");

                  if (policyLabel) {
                    policyLabel.style.color = "#0f172a";
                  }

                  const viewAction =
                    event.currentTarget.querySelector("[data-view-action]");

                  if (viewAction) {
                    viewAction.style.color = "#64748b";
                  }
                }}
                onFocus={(event) => {
                  event.currentTarget.style.outline = "2px solid #2563eb";
                  event.currentTarget.style.outlineOffset = "-2px";
                  event.currentTarget.style.background = "#eff6ff";
                  event.currentTarget.style.boxShadow = "inset 3px 0 0 #2563eb";
                }}
                onBlur={(event) => {
                  event.currentTarget.style.outline = "";
                  event.currentTarget.style.outlineOffset = "";
                  event.currentTarget.style.background = "";
                  event.currentTarget.style.boxShadow = "";
                }}
              >
                <td style={styles.td}>
                  <span data-policy-label style={styles.policyLinkText}>
                    {row.policyno || "—"}
                  </span>
                </td>

                <td style={styles.td}>
                  <span style={styles.mailText}>{row.mail_from_address || "—"}</span>
                </td>

                <td style={styles.td}>
                  {row.document_type === "Multiple types" ? (
                    <span style={styles.multiTypeBadge}>{row.document_type}</span>
                  ) : (
                    <TruncatedBadgeWithTooltip
                      text={row.document_type}
                      badgeStyle={styles.emailBadge}
                    />
                  )}
                </td>

                <td style={styles.td}>
                  <span style={styles.dateText}>
                    {row.verified_at
                      ? new Date(row.verified_at).toLocaleDateString()
                      : "—"}
                  </span>
                </td>

                <td style={styles.td}>
                  <span style={styles.documentCountBadge}>
                    {row.documentCount}
                  </span>
                </td>

                <td style={styles.tdAction}>
                  <span data-view-action style={styles.viewAction}>
                    View
                    <ChevronRight size={14} strokeWidth={2.5} aria-hidden />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableTable>
      );
    },
    [policySummaryRows, loading, handlePolicyRowSelect, handlePolicyRowKeyDown],
  );

  const renderDetailView = useCallback(() => {
    const documentCount = policyDocuments.length;

    return (
      <div style={styles.detailView}>
        <button
          type="button"
          style={styles.backLink}
          onClick={handleBackToResults}
        >
          ← BACK TO RESULTS
        </button>

        <div style={styles.policyHeaderCard}>
          <div style={styles.policyHeaderLabel}>POLICY NUMBER</div>

          <div style={styles.policyHeaderValue}>
            {selectedPolicy || "—"}
          </div>

          <div style={styles.policyHeaderCount} aria-live="polite">
            {documentCount}{" "}
            {documentCount === 1 ? "document" : "documents"} associated with
            this policy
          </div>
        </div>

        {documentCount === 0 ? (
          <div style={styles.empty}>No documents found for this policy</div>
        ) : (
          <ScrollableTable style={styles.detailTableWrap}>
            <table style={styles.detailTable}>
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "32%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={styles.th}>DOCUMENT TYPE</th>
                  <th style={styles.th}>DOCUMENT DETAILS</th>
                  <th style={styles.th}>ENTRY DATE</th>
                  <th style={styles.th}>EMAIL SUBJECT</th>
                  <th style={styles.th}>FILES</th>
                </tr>
              </thead>

              <tbody>
                {messageGroups.map((group) => {
                  const isExpanded = expandedMessages[group.groupKey] ?? true;
                  const fileCount = group.files.length;

                  return (
                    <React.Fragment key={group.groupKey}>
                      <tr style={styles.messageRow}>
                        <td style={styles.td}>
                          <TruncatedBadgeWithTooltip
                            text={group.document_type}
                            badgeStyle={styles.emailBadge}
                          />
                        </td>

                        <td style={styles.td}>
                          <div style={styles.docBlock}>
                            <div style={styles.docType}>
                              {group.policyno || "—"}
                            </div>

                            <div style={styles.verifyText}>
                              Verified:{" "}
                              {group.verified_at
                                ? new Date(group.verified_at).toLocaleString()
                                : "—"}
                            </div>
                          </div>
                        </td>

                        <td style={styles.td}>
                          <span style={styles.dateText}>
                            {group.verified_at
                              ? new Date(group.verified_at).toLocaleDateString()
                              : "—"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.subjectCell}>
                            {group.email_subject || "—"}
                          </div>

                          <div style={styles.mailText}>
                            {group.mail_from_address || "—"}
                          </div>
                        </td>

                        <td style={styles.td}>
                          <button
                            type="button"
                            style={styles.filesToggleBtn}
                            aria-expanded={isExpanded}
                            aria-controls={`message-files-${group.groupKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`}
                            onClick={() => toggleMessageExpand(group.groupKey)}
                          >
                            {fileCount}{" "}
                            {fileCount === 1 ? "file" : "files"}
                            {isExpanded ? (
                              <ChevronUp size={14} aria-hidden />
                            ) : (
                              <ChevronDown size={14} aria-hidden />
                            )}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && fileCount > 0 && (
                        <tr>
                          <td colSpan={5} style={styles.attachmentRowTd}>
                            <div
                              id={`message-files-${group.groupKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`}
                              style={styles.attachmentPanel}
                            >
                              {group.files.map((file, fileIndex) => (
                                <div
                                  key={`${group.groupKey}-${file.file_path}-${fileIndex}`}
                                  style={styles.attachmentCard}
                                >
                                  <div style={styles.attachmentCardLeft}>
                                    <FileText size={16} aria-hidden />

                                    <div style={styles.attachmentDetails}>
                                      <div style={styles.attachmentNameRow}>
                                        <span
                                          style={
                                            file.row_type === "EMAIL"
                                              ? styles.emailBadge
                                              : styles.attachmentBadge
                                          }
                                        >
                                          {file.row_type === "EMAIL"
                                            ? "Email"
                                            : "Attachment"}
                                        </span>

                                        <span
                                          style={styles.attachmentName}
                                          onMouseEnter={(e) =>
                                            showFileTooltip(e, file.file_name)
                                          }
                                          onMouseLeave={hideFileTooltip}
                                        >
                                          {file.file_name || "—"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <div style={styles.actionRow}>
                                    <button
                                      type="button"
                                      title="Preview document"
                                      style={styles.previewButton}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handlePreview(file.file_path);
                                      }}
                                    >
                                      Preview
                                    </button>

                                    {file.effective_access === "WRITE" && (
                                      <button
                                        type="button"
                                        title="Download document"
                                        style={styles.downloadButton}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleDownload(file.file_path);
                                        }}
                                      >
                                        Download
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </ScrollableTable>
        )}
      </div>
    );
  }, [
    policyDocuments,
    messageGroups,
    expandedMessages,
    selectedPolicy,
    handleBackToResults,
    handlePreview,
    handleDownload,
    toggleMessageExpand,
    showFileTooltip,
    hideFileTooltip,
  ]);

  /* =========================
     UI
  ========================= */

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Search Documents</h2>

            <p style={styles.sub}>
              Search verified classification documents and attachments.
            </p>
          </div>
        </div>

        {/* SEARCH */}

        <div style={styles.searchCard}>
          <div style={styles.searchRow}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="search-policy-number">
                Policy Number
              </label>

              <div ref={policyDropdownRef} style={styles.searchableWrapper}>
                <input
                  id="search-policy-number"
                  type="text"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showPolicyDropdown}
                  aria-controls="policy-suggestion-list"
                  style={styles.input}
                  placeholder="e.g. CFP 0975909293 or 097"
                  value={policyNo}
                  onFocus={() => {
                    if (policySuggestions.length > 0) {
                      setShowPolicyDropdown(true);
                    }
                  }}
                  onChange={handlePolicyInputChange}
                  onKeyDown={handlePolicyInputKeyDown}
                />

                {showPolicyDropdown && (
                  <div
                    id="policy-suggestion-list"
                    role="listbox"
                    style={styles.policyDropdown}
                    className="scrollable"
                  >
                    {suggestLoading ? (
                      <div style={styles.policyOptionEmpty}>
                        Loading suggestions...
                      </div>
                    ) : policySuggestions.length === 0 ? (
                      <div style={styles.policyOptionEmpty}>
                        No policies found
                      </div>
                    ) : (
                      policySuggestions.map((suggestion, index) => (
                        <div
                          key={suggestion}
                          role="option"
                          aria-selected={activeSuggestionIndex === index}
                          style={{
                            ...styles.policyOption,
                            ...(activeSuggestionIndex === index
                              ? styles.policyOptionActive
                              : null),
                          }}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                          onClick={() =>
                            handlePolicySuggestionSelect(suggestion)
                          }
                        >
                          {suggestion}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <span style={styles.fieldFooter} aria-live="polite">
                {suggestLoading ? "Loading suggestions..." : ""}
              </span>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Mail From</label>

              <input
                style={styles.input}
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Primary Insured</label>

              <input
                style={styles.input}
                placeholder="Enter insured"
                value={insured}
                onChange={(e) => setInsured(e.target.value)}
              />

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Agency Number</label>

              <input
                style={styles.input}
                placeholder="Enter agency number"
                value={agencyNo}
                onChange={(e) => setAgencyNo(e.target.value)}
              />

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>

            <div style={styles.buttonField}>
              <label style={styles.labelHidden} aria-hidden="true">
                Search
              </label>

              <button
                style={styles.button}
                onClick={runSearch}
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* ERROR */}

        {error && <div style={styles.error}>{error}</div>}

        {/* RESULTS */}

        {hasSearched && (
          <div style={{ marginTop: "24px" }}>
            {selectedPolicy !== null ? (
              renderDetailView()
            ) : (
              <>
                {!loading && (
                  <div style={styles.resultCountBlock} aria-live="polite">
                    <div style={styles.resultCount}>
                      {policySummaryRows.length}{" "}
                      {policySummaryRows.length === 1 ? "POLICY" : "POLICIES"}
                    </div>
                    <div style={styles.resultCountSub}>
                      {results.length}{" "}
                      {results.length === 1 ? "document" : "documents"} total
                    </div>
                    {policySummaryRows.length > 0 && (
                      <div style={styles.resultHint}>
                        Click a policy row to view its documents
                      </div>
                    )}
                  </div>
                )}

                <ResponsiveTableCards
                  data={policySummaryRows}
                  cardMapper={mapPolicyCard}
                  onCardClick={handlePolicyRowSelect}
                  renderTable={renderResultsTable}
                  loading={loading}
                  emptyTitle="No results found"
                  emptySub="Try adjusting your search criteria."
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* PREVIEW */}

      {fileTooltip &&
        createPortal(
          <div
            style={{
              ...styles.fileTooltip,
              top: fileTooltip.top,
              left: fileTooltip.left,
            }}
            role="tooltip"
          >
            {fileTooltip.text}
            <span style={styles.fileTooltipArrow} aria-hidden />
          </div>,
          document.body,
        )}

      {isPreviewOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <span>Preview</span>

              <button onClick={closePreview} style={styles.closeButton}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody} className="scrollable">
              {previewError && <div>{previewError}</div>}
              {previewUrl && (
                <iframe
                  title="Preview"
                  src={previewUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   STYLES
========================= */

const styles = {
  container: {
    // padding: "clamp(12px, 4vw, 24px)",
    // background: "#f8fafc",
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    // overflowX: "hidden",
  },

  readBadge: {
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.04em",
  },

  writeBadge: {
    background: "#dcfce7",
    color: "#166534",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.04em",
  },

  card: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "clamp(16px, 5vw, 24px)",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    width: "100%",
    boxSizing: "border-box",
  },

  header: {
    marginBottom: "20px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  searchCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "clamp(12px, 4vw, 18px)",
    width: "100%",
    boxSizing: "border-box",
  },

  searchRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "clamp(12px, 3vw, 16px)",
    alignItems: "start",
    width: "100%",
    boxSizing: "border-box",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
  },

  searchableWrapper: {
    position: "relative",
    width: "100%",
  },

  policyDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #dbe2ea",
    borderRadius: "10px",
    marginTop: "6px",
    maxHeight: "220px",
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 1000,
    boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
  },

  policyOption: {
    padding: "12px 14px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "14px",
    color: "#0f172a",
    textAlign: "left",
  },

  policyOptionActive: {
    background: "#f8fafc",
  },

  policyOptionEmpty: {
    padding: "12px 14px",
    color: "#64748b",
    fontSize: "13px",
    textAlign: "left",
  },

  resultCount: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
  },

  resultCountBlock: {
    marginBottom: "12px",
  },

  resultCountSub: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#64748b",
  },

  resultHint: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#475569",
    fontWeight: "500",
  },

  documentCountBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "28px",
    padding: "4px 10px",
    borderRadius: "999px",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: "600",
  },

  multiTypeBadge: {
    background: "#f1f5f9",
    color: "#475569",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "600",
  },

  buttonField: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
  },

  label: {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "6px",
    color: "#475569",
    textTransform: "uppercase",
  },

  input: {
    padding: "12px 14px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    fontSize: "14px",
    background: "#fff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },

  labelHidden: {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "6px",
    color: "transparent",
    textTransform: "uppercase",
    userSelect: "none",
  },

  fieldFooter: {
    display: "block",
    marginTop: "4px",
    fontSize: "11px",
    lineHeight: "16px",
    minHeight: "16px",
    color: "#64748b",
  },

  button: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    height: "46px",
    width: "100%",
    boxSizing: "border-box",
  },

  error: {
    marginTop: "20px",
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "10px",
  },

  tableWrap: {
    overflowX: "auto",
  },

  detailTableWrap: {
    overflowX: "auto",
    marginTop: "8px",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  detailTable: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    minWidth: "860px",
  },

  messageRow: {
    background: "#ffffff",
  },

  filesToggleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#eff6ff",
    color: "#2563eb",
    border: "1px solid #bfdbfe",
    borderRadius: "10px",
    padding: "8px 12px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  attachmentRowTd: {
    padding: "0",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  attachmentPanel: {
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  attachmentCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 14px",
  },

  attachmentCardLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    minWidth: 0,
    flex: 1,
  },

  attachmentDetails: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },

  attachmentNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    minWidth: 0,
  },

  attachmentName: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
    wordBreak: "break-word",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  th: {
    textAlign: "center",
    whiteSpace: "nowrap",
    padding: "10px 8px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "600",
  },

  td: {
    padding: "10px 8px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
    overflow: "hidden",
    fontSize: "13px",
    textAlign: "center",
  },

  row: {
    transition: "background 0.2s ease",
  },

  clickableRow: {
    transition: "background 0.15s ease, box-shadow 0.15s ease",
    cursor: "pointer",
  },

  thAction: {
    textAlign: "center",
    padding: "10px 8px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    width: "72px",
  },

  tdAction: {
    padding: "10px 8px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  policyLinkText: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
    transition: "color 0.15s ease",
  },

  viewAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    transition: "color 0.15s ease",
  },

  detailView: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  backLink: {
    alignSelf: "flex-start",
    background: "transparent",
    border: "none",
    padding: 0,
    color: "#475569",
    fontSize: "12px",
    fontWeight: "600",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    cursor: "pointer",
  },

  policyHeaderCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "clamp(16px, 4vw, 24px)",
  },

  policyHeaderLabel: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "8px",
  },

  policyHeaderValue: {
    fontSize: "clamp(24px, 4vw, 32px)",
    fontWeight: "700",
    color: "#0f172a",
    lineHeight: 1.2,
    marginBottom: "8px",
  },

  policyHeaderCount: {
    fontSize: "14px",
    color: "#64748b",
  },

  emailBadge: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "600",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    boxSizing: "border-box",
  },

  attachmentBadge: {
    background: "#ede9fe",
    color: "#6d28d9",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "600",
  },

  policyText: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
  },

  cellText: {
    fontSize: "13px",
    color: "#334155",
    wordBreak: "break-word",
  },

  docBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  docType: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
  },

  verifyText: {
    fontSize: "12px",
    color: "#64748b",
  },

  dateText: {
    fontSize: "13px",
    color: "#475569",
  },

  subjectCell: {
    fontSize: "13px",
    lineHeight: 1.4,
    fontWeight: "400",
    marginBottom: "6px",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  mailText: {
    fontSize: "12px",
    color: "#64748b",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },

  fileBadgeWrap: {
    position: "relative",
    display: "inline-block",
    maxWidth: "100%",
  },

  fileBadge: {
    background: "#f1f5f9",
    padding: "6px 8px",
    borderRadius: "8px",
    fontSize: "12px",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },

  fileTooltip: {
    position: "fixed",
    transform: "translate(-50%, calc(-100% - 10px))",
    background: "#1e293b",
    color: "#f8fafc",
    padding: "8px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "1.5",
    whiteSpace: "normal",
    wordBreak: "break-all",
    maxWidth: "260px",
    minWidth: "120px",
    textAlign: "center",
    boxShadow: "0 6px 20px rgba(15,23,42,0.18)",
    zIndex: 10000,
    pointerEvents: "none",
  },

  fileTooltipArrow: {
    position: "absolute",
    bottom: "-4px",
    left: "50%",
    transform: "translateX(-50%) rotate(45deg)",
    width: "8px",
    height: "8px",
    background: "#1e293b",
    borderRadius: "1px",
  },

  actionRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
  },

  previewButton: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "6px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
  },

  downloadButton: {
    background: "#fff",
    color: "#0f172a",
    border: "1px solid #cbd5e1",
    padding: "6px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
  },

  empty: {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  modal: {
    width: "90%",
    height: "90%",
    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  modalHeader: {
    padding: "16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "600",
  },

  modalBody: {
    flex: 1,
    overflow: "auto",
    padding: "16px",
  },

  closeButton: {
    border: "none",
    background: "transparent",
    fontSize: "18px",
    cursor: "pointer",
  },
};

function TruncatedBadgeWithTooltip({ text, badgeStyle }) {
  const label = text || "—";
  const textRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const showTooltip = useCallback(
    (event) => {
      const el = textRef.current;

      if (!el || el.scrollWidth <= el.clientWidth) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();

      setTooltip({
        text: label,
        top: rect.top,
        left: rect.left + rect.width / 2,
      });
    },
    [label],
  );

  const hideTooltip = useCallback(() => {
    setTooltip(null);
  }, []);

  useEffect(() => {
    if (!tooltip) {
      return undefined;
    }

    const hide = () => setTooltip(null);

    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [tooltip]);

  return (
    <>
      <span
        ref={textRef}
        style={badgeStyle}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {label}
      </span>

      {tooltip &&
        createPortal(
          <div
            style={{
              ...styles.fileTooltip,
              top: tooltip.top,
              left: tooltip.left,
            }}
            role="tooltip"
          >
            {tooltip.text}
            <span style={styles.fileTooltipArrow} aria-hidden />
          </div>,
          document.body,
        )}
    </>
  );
}
